import { AppBreadcrumb, AppButton, AppCard, AppContainer } from '@/components/atoms';
import { InsurerForm } from '@/components/organisms';
import { LoadingTemplate } from '@/components/templates';
import { useCreateInsurerMutation, useInsurerFormInitData } from '@/hooks/admin';
import useAppToast from '@/hooks/useAppToast';
import { InsurerFormData, insurerFormSchema } from '@/schemas/insurerFormSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useEffect } from 'react';
import { FieldErrors, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Flex } from 'antd';

const SiteAdminCreateInsurerPage = () => {
  const navigate = useNavigate();
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';

  const toast = useAppToast();
  const { data: initData, isLoading } = useInsurerFormInitData();
  const { createInsurerMutation, isPending } = useCreateInsurerMutation(currentSite);

  const methods = useTrimmedForm<InsurerFormData>({
    resolver: zodResolver(insurerFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      insurer_number: '',
      sub_number: '',
      insurer_type_id: 0,
      insurance_kind_id: 0,
      insurer_name_kana: '',
      insurer_name: '',
      staff_department: '',
      delegation_insurer_name: '',
      postal_code1: '',
      postal_code2: '',
      address1: '',
      address2: '',
      address3: '',
      telephone: '',
      available_from: '',
      available_to: '',
      cost_rate: '',
      family_cost_rate: '',
      is_special: 0,
      is_enabled_invoice: null,
      medix_claim_type_id: 0,
      medix_visit_detail_report_id: 0,
      upsystem_visit_detail_report_id: 0,
      calc_type: null,
      limit_amount: '',
      limit_num_month: '',
      limit_amount_month: '',
      is_adopted_lower_cost: 0,
    },
  });

  // Set dropdown defaults from initData once loaded
  useEffect(() => {
    if (!initData) return;
    if (initData.insurer_types.length > 0) {
      methods.setValue('insurer_type_id', initData.insurer_types[0].id);
    }
    if (initData.insurance_kinds.length > 0) {
      methods.setValue('insurance_kind_id', initData.insurance_kinds[0].id);
    }
    if (initData.claim_types.length > 0) {
      methods.setValue('medix_claim_type_id', initData.claim_types[0].id);
    }
  }, [initData, methods]);

  const breadcrumbItems = [
    { title: '保険者', href: `/${currentSite}/admin/insurers` },
    { title: '新規' },
  ];

  const onValidationError = (_errors: FieldErrors<InsurerFormData>) => {
    toast.error('エラーが発生しました。');
  };

  const onSubmit = async (data: InsurerFormData) => {
    const sanitizedData = {
      ...data,
      cost_rate: (data.cost_rate === '' || data.cost_rate === undefined) ? '0' : data.cost_rate,
      family_cost_rate: (data.family_cost_rate === '' || data.family_cost_rate === undefined) ? '0' : data.family_cost_rate,
      receipt_delegation_from: data.receipt_delegation_from || undefined,
      receipt_delegation_to: data.receipt_delegation_to || undefined,
      available_to: data.available_to || undefined,
    };
    try {
      await createInsurerMutation(sanitizedData);
      navigate(`/${currentSite}/admin/insurers`);
    } catch (error) {
      const apiError = error as { response?: { data?: { errors?: Record<string, string[]> } } };
      const errors = apiError.response?.data?.errors;
      if (errors?.insurer_number) {
        const message = errors.insurer_number[0];
        methods.setError('insurer_number', {
          type: 'server',
          message,
        });
        methods.setError('sub_number', {
          type: 'server',
          message,
        });
      }
      if (errors?.insurer_type_id) {
        toast.error(errors.insurer_type_id[0]);
        methods.setValue('insurer_type_id', initData?.insurer_types[0]?.id ?? 0);
      }
      if (errors?.insurance_kind_id) {
        toast.error(errors.insurance_kind_id[0]);
        methods.setValue('insurance_kind_id', initData?.insurance_kinds[0]?.id ?? 0);
      }
      if (errors?.medix_claim_type_id) {
        toast.error(errors.medix_claim_type_id[0]);
        methods.setValue('medix_claim_type_id', initData?.claim_types[0]?.id ?? 0);
      }
    }
  };

  if (isLoading || !initData) {
    return <LoadingTemplate />;
  }

  return (
    <AppContainer className="py-6 space-y-6">
      <AppBreadcrumb items={breadcrumbItems} />
      <h3 className="text-xl">保険者作成</h3>
      <AppCard title="基本情報">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onValidationError)}>
            <InsurerForm initData={initData} />
            <Flex justify="end">
              <AppButton type="primary" htmlType="submit" loading={isPending}>
                <span className="px-6">更新</span>
              </AppButton>
            </Flex>
          </form>
        </FormProvider>
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminCreateInsurerPage;
